import { Container, Typography } from "@mui/material";
import { ROUTES } from "domain/configuration";
import unboundActions from "domain/core/actions";
import {
  getBillEstimate,
  getBillEstimateFetchStatus,
  getBillEstimates,
  getMonthlyElectricityUsage,
  getSelectedProduct,
  getSignupLitres,
} from "domain/core/selectors";
import {
  CustomerFacingProductByline,
  CustomerFacingProductName,
  ProductName,
} from "domain/core/types";
import { sendAnalyticsPageView } from "lib/util";
import { useActions, useReduxState } from "re-reduced";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import { BackButton } from "ui/components/BackButton";
import Button from "ui/components/Button";
import { MLink } from "ui/components/StyledComponents";
import { EvPlan } from "ui/compounds/planDetails/EvPlan";
import { FuelRewardsPlan } from "ui/compounds/planDetails/FuelRewardsPlan";
import PricingAndPlanDetailsDialog from "ui/compounds/PricingAndPlanDetailsDialog";
import { PADDING } from "ui/theme";

const TextContainer = styled.div`
  display: flex;
  align-items: baseline;
`;

const SInlineTypography = styled(Typography)`
  margin-left: ${PADDING.large};
`;

const stateSelectorMap = {
  billEstimate: getBillEstimate,
  billEstimates: getBillEstimates,
  billEstimateFetchStatus: getBillEstimateFetchStatus,
  campaignLitres: getSignupLitres,
  monthlyUsage: getMonthlyElectricityUsage,
  selectedProduct: getSelectedProduct,
};

export const PlanDetails: React.FC = () => {
  const reduxState = useReduxState(stateSelectorMap);
  const [usagePeriod, setUsagePeriod] = useState<number>(30);
  const [planDetailsModalOpen, setPlanDetailsModalOpen] =
    useState<boolean>(false);
  const [GSTInclusive, setGSTInclusive] = useState<boolean>(false);

  const actions = useActions(unboundActions);

  useEffect(() => {
    sendAnalyticsPageView(
      `${CustomerFacingProductName[reduxState.selectedProduct]} plan`,
    );
  }, [reduxState.selectedProduct]);

  const unselectedProduct = useCallback(() => {
    const products =
      reduxState.billEstimates?.map((estimate) => estimate.product_name) ||
      Object.values(ProductName);
    return products.find((product) => product !== reduxState.selectedProduct);
  }, [reduxState.billEstimates, reduxState.selectedProduct]);

  const handlePricingAndPlanDialogOpen = useCallback(() => {
    setPlanDetailsModalOpen(true);
  }, []);

  const handlePricingAndPlanDialogClose = useCallback(() => {
    setPlanDetailsModalOpen(false);
  }, []);

  const handleConfirmSignUp = useCallback(() => {
    actions.updateFlickSignUp();
    actions.setCurrentFormStep("/details?step=about_you");
  }, []);

  const handleSwitchPlan = useCallback(() => {
    actions.setSelectedProduct(unselectedProduct());
  }, [reduxState.selectedProduct]);

  return (
    <>
      <Container component="main">
        <Typography variant="body2" component="p">
          Step 2 of{" "}
          {reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS
            ? "6"
            : "5"}
        </Typography>
        <BackButton formStep={"/"} />
        <TextContainer>
          <Typography variant="h2">
            {CustomerFacingProductName[reduxState.selectedProduct]} plan
          </Typography>
          {unselectedProduct() && (
            <SInlineTypography>
              Switch to{" "}
              <MLink
                component="button"
                id="button-switch-plan"
                onClick={handleSwitchPlan}
              >
                {CustomerFacingProductName[unselectedProduct()]} plan
              </MLink>
            </SInlineTypography>
          )}
        </TextContainer>
        <Typography
          variant="h3"
          color="textPrimary"
          sx={{
            paddingTop: PADDING.xSmall,
          }}
        >
          {CustomerFacingProductByline[reduxState.selectedProduct]}
        </Typography>

        {reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS && (
          <FuelRewardsPlan
            onPlanDetailsDialogOpen={handlePricingAndPlanDialogOpen}
            usagePeriod={usagePeriod}
            setUsagePeriod={setUsagePeriod}
            GSTInclusive={GSTInclusive}
            setGSTInclusive={setGSTInclusive}
          />
        )}

        {reduxState.selectedProduct === ProductName.Z_EV && (
          <EvPlan
            GSTInclusive={GSTInclusive}
            setGSTInclusive={setGSTInclusive}
          />
        )}
        <Button
          variant="contained"
          id="button-confirmed-next"
          component={Link}
          to={ROUTES.details}
          onClick={handleConfirmSignUp}
        >
          Join now
        </Button>
        <PricingAndPlanDetailsDialog
          visible={planDetailsModalOpen}
          onClose={handlePricingAndPlanDialogClose}
          billEstimate={reduxState.billEstimate}
          GSTInclusive={GSTInclusive}
          usagePeriodValue={usagePeriod}
          productName={reduxState.selectedProduct}
        />
      </Container>
    </>
  );
};
